import { ActionIcon, Box, Indicator, Tooltip } from '@mantine/core';
import type { QueryObserverResult, UseBaseMutationResult } from '@tanstack/react-query';
import * as React from 'react';
import { MouseEventHandler, useMemo, useState } from 'react';
import { useNestedDialogFunction } from '../../../../../react-commons/dialog-provider/UseNestedDialogFunction';
import { OpenModal } from '../../../../../react-commons/utils/modals/openModal';
import { ControlledInputs } from '../../../../../react-commons/utils/read-only-inputs/controlledInputs';
import { compareByName, formatToPascalCase } from '../../../../../react-commons/utils/utilFunctions';
import { ResourceType } from '../../../resource/enumerations/ResourceType';
import { DeleteMutateObject } from '../../../resource/resource-base/mutateObjects';
import { ValidationScopes } from '../../../resource/resource-base/types';
import { DetailedValidationResource } from '../../../resource/resources/validation/detailedValidationResource';
import { MutationValidationResource } from '../../../resource/resources/validation/mutationValidationResource';
import { useSystemInformationActions } from '../../../resource/services/systemInformationService';
import {
    type EnumProperties,
    formatTypePropertyName,
    type OverviewComponentPropertyValidationInformation,
} from '../../util/overviewUtils';
import { getPropertyTableLabel } from '../../util/utilFunctions';
import { getDialogSizeForResourceType } from '../dialogs/getDialogSizeForResourceType';
import { ResizableDialogWithValidation } from '../dialogs/ResizableDialogWithValidation';
import { useDeleteResourceDialogFunction } from '../dialogs/UseDeleteResourceDialogFunction';
import { MRT_RowSelectionState } from 'mantine-react-table';
import { ValidationEntrySeverity } from '../../../../../react-commons/components/ValidationEntrySeverity';
import { MantineColumn } from '../../../../../react-commons/components/table/MantineTable';
import {
    IndirectlyVersionedArtifactResource,
    NamedArtifactResource,
} from '../../../resource/resource-base/artifactResourceTypes';
import { DeepKeys } from '@tanstack/react-table';
import { formatEnum } from '../../../resource/enumerations/utils/formatEnum';
import { ContextMenuItemOptions, useContextMenu } from 'mantine-contextmenu';
import { IconDotsVertical } from '@tabler/icons-react';
import { OverviewCrudValidationTable } from '../tables/OverviewCrudValidationTable';

export const OverviewComponent = <E extends string, R extends IndirectlyVersionedArtifactResource>({
                                                                                                       resourceQuery,
                                                                                                       validateResources,
                                                                                                       deleteFunction,
                                                                                                       content,
                                                                                                       label,
                                                                                                       propertyValidationInformation,
                                                                                                       enumProperties,
                                                                                                       unusedIdParams,
                                                                                                       onBatchAdd,
                                                                                                   }: {
    resourceQuery: QueryObserverResult<Array<R> | null>;
    validateResources: QueryObserverResult<Record<string, DetailedValidationResource>>;
    deleteFunction: UseBaseMutationResult<MutationValidationResource | null, unknown, DeleteMutateObject>;
    content: (id: string, displayedInOverview: boolean) => React.ReactNode;
    propertyValidationInformation: OverviewComponentPropertyValidationInformation;
    enumProperties?: EnumProperties<E, R>;
    unusedIdParams?: { resourceType: ResourceType; parentId: string };
    onBatchAdd?: () => void;
    label: string;
    withAffectedMappings?: boolean;
    validationScope: ValidationScopes;
}) => {
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
    const [validationFilter, setValidationFilter] = useState<Array<ValidationEntrySeverity> | null>(null);

    const deleteResourceDialogFunction = useDeleteResourceDialogFunction();
    const nestedDialogFunction = useNestedDialogFunction();
    const { showContextMenu } = useContextMenu();

    const allPropertyDetails = useSystemInformationActions().useGetAllPropertyDetails(
        propertyValidationInformation.resourceType,
    );

    const columns = useMemo((): Array<MantineColumn<R>> => {
        const result: Array<MantineColumn<R>> = [];

        if (propertyValidationInformation.namePropertyIdentifier) {
            result.push({
                type: 'string',
                accessorKey: 'name' as DeepKeys<R>,
                header: 'Name',
                Header: getPropertyTableLabel(
                    allPropertyDetails,
                    propertyValidationInformation.namePropertyIdentifier,
                    'Name',
                ),
                Cell: ({ row }) => {
                    if (!('name' in row.original) || typeof row.original.name !== 'string') {
                        throw new Error(
                            `${propertyValidationInformation.resourceType} does not have a ${propertyValidationInformation.namePropertyIdentifier}!`,
                        );
                    }
                    return <ControlledInputs.String nullable={false} value={row.original.name} readOnly={true} />;
                },
            });
        }
        if (enumProperties && propertyValidationInformation.typeOrCategoryPropertyIdentifier) {
            result.push({
                type: enumProperties.enumType,
                formatEnum,
                accessorKey: enumProperties.enumPropertyName,
                header: formatTypePropertyName(enumProperties.enumPropertyName),
                Header: getPropertyTableLabel(
                    allPropertyDetails,
                    propertyValidationInformation.typeOrCategoryPropertyIdentifier,
                    formatTypePropertyName(enumProperties.enumPropertyName),
                ),
                Cell: ({ row }) => (
                    <ControlledInputs.String
                        nullable={false}
                        value={formatEnum(
                            enumProperties.enumType,
                            row.original[enumProperties.enumPropertyName] as string,
                        )}
                        readOnly={true}
                    />
                ),
            });
        }

        return result;
    }, [
        allPropertyDetails,
        enumProperties,
        propertyValidationInformation.resourceType,
        propertyValidationInformation.namePropertyIdentifier,
        propertyValidationInformation.typeOrCategoryPropertyIdentifier,
    ]);

    const data = useMemo(() => {
        let result = resourceQuery.data;

        if (!result) {
            return result;
        }

        if (enumProperties?.enumFilter) {
            result = result.filter((r) => r[enumProperties.enumPropertyName] === enumProperties.enumFilter);
        }

        if (validationFilter && validationFilter.length > 0) {
            result = result.filter((value) => {
                const maxSeverity = validateResources.data?.[value.id]?.maxSeverity;
                return maxSeverity && validationFilter.includes(maxSeverity);
            });
        }

        if (propertyValidationInformation.namePropertyIdentifier) {
            result = (result as unknown as Array<NamedArtifactResource>).toSorted(compareByName) as unknown as Array<R>;
        }

        return result;
    }, [
        resourceQuery.data,
        enumProperties?.enumFilter,
        enumProperties?.enumPropertyName,
        validationFilter,
        validateResources.data,
        propertyValidationInformation.namePropertyIdentifier,
    ]);

    const validationData = useMemo(() => {
        if (!data || !validateResources.data) {
            return null;
        }
        return Object.fromEntries(Object.entries(validateResources.data).filter(([k]) => data.find((r) => r.id === k)));
    }, [data, validateResources.data]);

    const openResourceEditDialog = (resource: R) => {
        nestedDialogFunction.showNestedDialog(
            <ResizableDialogWithValidation
                title={`Edit ${formatToPascalCase(propertyValidationInformation.resourceType)}`}
                dialogSize={getDialogSizeForResourceType(propertyValidationInformation.resourceType)}
                disableBackdropClick={true}
                onClose={nestedDialogFunction.closeNestedDialog}
                canClose={true}
                resourceType={propertyValidationInformation.resourceType}
                metaDataInfo={resource}
            >
                {content(resource.id, false)}
            </ResizableDialogWithValidation>,
        );
    };

    const onEllipsis: MouseEventHandler<HTMLButtonElement> = (e) => {
        const commonMenuItems: Array<ContextMenuItemOptions> = [];

        if (validateResources.data && Object.keys(validateResources.data).length) {
            commonMenuItems.push({
                key: 'Filter by Validation Severity',
                title: (
                    <Indicator position={'middle-start'} size={5} offset={-5} disabled={!validationFilter}>
                        Filter by Validation Severity
                    </Indicator>
                ),
                onClick: () => {
                    if (validationFilter) {
                        setValidationFilter(null);
                    } else {
                        OpenModal.SeveritySelectionModal(
                            'Filter By Validation Severity',
                            setValidationFilter,
                            undefined,
                            validateResources.data?.let((value) => Object.values(value)),
                            (value: DetailedValidationResource | undefined, validationEntrySeverity) =>
                                value?.maxSeverity === validationEntrySeverity,
                        );
                    }
                },
            });
        }

        showContextMenu(commonMenuItems)(e);
    };


    return (
        <Box
            style={{
                height: '100%',
                minHeight: 300,
                position: 'relative',
            }}
        >
            <OverviewCrudValidationTable
                openWithoutValidation={true}
                columns={columns}
                data={data}
                validationData={validationData}
                validationIsLoading={validateResources.isLoading}
                getIdForValidation={(resource) => resource.id}
                resourceType={propertyValidationInformation.resourceType}
                label={label}
                onAdd={onBatchAdd}
                rowSelectionState={{ rowSelection, setRowSelection }}
                onEdit={(resource) => openResourceEditDialog(resource)}
                onDelete={(resource) => {
                    deleteResourceDialogFunction.deleteResource(
                        resource.id,
                        'name' in resource ? (resource.name as string) : null,
                        propertyValidationInformation.resourceType,
                        deleteFunction,
                    );
                }}
                initialPageSize={25}
                tableOptions={{
                    getRowId: (originalRow: R) => originalRow.id,
                    enableStickyHeader: true,
                    enableRowSelection: true,
                    enableMultiRowSelection: true,
                    enableSelectAll: true,
                    renderTopToolbarCustomActions:
                        (unusedIdParams ?? (validateResources.data && Object.keys(validateResources.data).length)) ?
                            () => (
                                <Tooltip label={'Additional filters'}>
                                    <Indicator size={7} offset={7} disabled={!validationFilter}>
                                        <ActionIcon variant={'subtle'} size={'lg'} color={'gray'} onClick={onEllipsis}>
                                            <IconDotsVertical />
                                        </ActionIcon>
                                    </Indicator>
                                </Tooltip>
                            )
                            : undefined,
                    mantinePaperProps: {
                        style: {
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            left: 0,
                            right: 0,
                            display: 'flex',
                            flexDirection: 'column',
                        },
                    },
                    mantineTableContainerProps: {
                        style: {
                            flexGrow: 1,
                        },
                    },
                }}
            />
        </Box>
    );
};
